<main class="operator-list-page">
  @if (isLoading && dashboard == null) {
    <section class="status-panel" aria-live="polite">
      <mat-spinner diameter="32" aria-hidden="true"></mat-spinner>
      <p>オペレーター一覧を読み込み中です。</p>
    </section>
  } @else if (dashboard == null) {
    <section class="status-panel error-panel" role="alert">
      <mat-icon aria-hidden="true">error_outline</mat-icon>
      <p>{{ loadErrorMessage }}</p>
      <button type="button" class="retry-button" (click)="retry()">
        再試行
      </button>
    </section>
  } @else {
    <header class="page-heading">
      <div>
        <p class="eyebrow">Personal Support Board</p>
        <h1>オペレーター一覧</h1>
        <p class="page-description">
          最新実績とアセスメントの変化から、優先して支援するオペレーターを確認します。
        </p>
      </div>
      <div class="page-heading-actions">
        <div class="page-meta">
          KPI 集計期間<br />
          <strong>{{ appliedPeriodFrom }} 〜 {{ appliedPeriodTo }}</strong>
        </div>
        @if (canOpenSettings) {
          <button
            type="button"
            class="btn btn-ghost btn-sm settings-button"
            (click)="openSettings()"
          >
            設定画面を開く
          </button>
        }
      </div>
    </header>

    @if (loadErrorMessage) {
      <div class="message error-message" role="alert" aria-live="assertive">
        <span>{{ loadErrorMessage }}</span>
        <button type="button" class="retry-button" (click)="retry()">
          再試行
        </button>
      </div>
    }
    @if (actionErrorMessage) {
      <div class="message error-message" role="alert" aria-live="assertive">
        {{ actionErrorMessage }}
      </div>
    }
    @if (periodValidationMessage) {
      <div class="message error-message" role="alert">
        {{ periodValidationMessage }}
      </div>
    }

    <section class="operator-tools" aria-label="一覧の表示設定と絞り込み">
      <div class="tool-section">
        <div class="tool-section-label">表示設定</div>
        <div class="operator-setting-row">
          <div id="month-range-picker" class="filter-field popover-field">
            <span id="kpi-period-label" class="filter-label">KPI 集計期間</span>
            <button
              id="kpi-period"
              class="picker-trigger"
              type="button"
              aria-labelledby="kpi-period-label kpi-period-value"
              aria-haspopup="dialog"
              [attr.aria-expanded]="isMonthRangeOpen"
              aria-controls="kpi-period-popover"
              (click)="toggleMonthRangePicker()"
            >
              <span id="kpi-period-value" class="picker-trigger-label">{{
                periodSelectionLabel()
              }}</span>
              <svg
                aria-hidden="true"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="m6 9 6 6 6-6" />
              </svg>
            </button>
            <div
              id="kpi-period-popover"
              class="filter-popover month-range-popover"
              role="dialog"
              aria-label="KPI 集計期間を選択"
              [hidden]="!isMonthRangeOpen"
            >
              <div class="month-picker-head">
                <button
                  id="month-picker-prev"
                  class="month-nav-button"
                  type="button"
                  aria-label="前年を表示"
                  (click)="changeDisplayedMonthYear(-1)"
                >
                  <svg
                    aria-hidden="true"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path d="m15 18-6-6 6-6" />
                  </svg>
                </button>
                <span class="month-picker-year"
                  >{{ displayedMonthYear }}年</span
                >
                <button
                  id="month-picker-next"
                  class="month-nav-button"
                  type="button"
                  aria-label="翌年を表示"
                  (click)="changeDisplayedMonthYear(1)"
                >
                  <svg
                    aria-hidden="true"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path d="m9 18 6-6-6-6" />
                  </svg>
                </button>
              </div>
              <p class="month-picker-help">
                {{
                  selectingPeriodEnd
                    ? '終了月を選択してください'
                    : '開始月を選択してください'
                }}
              </p>
              <div class="month-grid" aria-label="月の選択肢">
                @for (month of months; track month) {
                  <button
                    class="month-option"
                    type="button"
                    [attr.data-month]="month"
                    [attr.aria-label]="monthOptionLabel(month)"
                    [attr.aria-pressed]="isMonthInRange(month)"
                    [class.is-in-range]="isMonthInRange(month)"
                    [class.is-start]="isMonthStart(month)"
                    [class.is-end]="isMonthEnd(month)"
                    (click)="selectMonth(month)"
                  >
                    {{ month }}月
                  </button>
                }
              </div>
            </div>
          </div>

          <label class="filter-field">
            <span class="filter-label">スコア集計回数</span>
            <select
              [(ngModel)]="scoreCount"
              (ngModelChange)="onScoreCountChanged()"
            >
              @for (count of scoreCountOptions; track count) {
                <option [ngValue]="count">直近{{ count }}回</option>
              }
            </select>
          </label>
          <p class="independent-note">
            KPI は指定年月、スコアは現在からの直近回数で独立して集計します。
          </p>
        </div>
      </div>

      <div class="tool-section">
        <div class="tool-section-label">絞り込み</div>
        <div class="filter-primary-row">
          <label class="filter-field name-filter">
            <span class="filter-label">オペレーター名</span>
            <input
              type="search"
              [formControl]="nameControl"
              placeholder="氏名を入力"
              autocomplete="off"
            />
          </label>

          <div id="role-filter-dropdown" class="filter-field popover-field">
            <span id="role-filter-label" class="filter-label">役職</span>
            <button
              id="role-filter-trigger"
              class="picker-trigger"
              type="button"
              aria-labelledby="role-filter-label role-filter-summary"
              aria-haspopup="dialog"
              [attr.aria-expanded]="isRoleFilterOpen"
              aria-controls="role-filter-popover"
              (click)="toggleRoleFilter()"
            >
              <span id="role-filter-summary" class="picker-trigger-label">{{
                roleSelectionLabel()
              }}</span>
              <svg
                aria-hidden="true"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="m6 9 6 6 6-6" />
              </svg>
            </button>
            <div
              id="role-filter-popover"
              class="filter-popover role-filter-popover"
              role="dialog"
              aria-label="役職を複数選択"
              [hidden]="!isRoleFilterOpen"
            >
              <label class="role-option role-option-all">
                <input
                  #allRolesCheckbox
                  id="role-filter-all"
                  type="checkbox"
                  [checked]="allRolesSelected()"
                  [indeterminate]="someRolesSelected()"
                  (change)="onAllRoleSelectionChanged(allRolesCheckbox.checked)"
                />
                <span>すべての役職</span>
              </label>
              @for (role of roleOptions; track role.key) {
                <label class="role-option">
                  <input
                    #roleCheckbox
                    type="checkbox"
                    [value]="role.key"
                    [checked]="isRoleSelected(role.key)"
                    (change)="
                      onRoleOptionChanged(role.key, roleCheckbox.checked)
                    "
                  />
                  <span>{{ role.label }}</span>
                </label>
              }
            </div>
          </div>

          <label class="filter-field coaching-filter">
            <span class="filter-label">育成対象</span>
            <select
              [(ngModel)]="coachingFilter"
              (ngModelChange)="onFiltersChanged()"
            >
              <option value="all">全員</option>
              <option value="included">育成対象のみ</option>
              <option value="excluded">育成対象外のみ</option>
            </select>
          </label>

          <label class="filter-field report-to-filter">
            <span class="filter-label">上司</span>
            <select
              [(ngModel)]="reportToSelectValue"
              [disabled]="reportToOptions.length === 0"
            >
              <option value="">
                {{
                  reportToOptions.length === 0
                    ? '上司が設定されていません'
                    : '全員'
                }}
              </option>
              @for (option of reportToOptions; track option.userId) {
                <option [value]="option.userId">{{ option.userName }}</option>
              }
            </select>
          </label>

          <div
            class="filter-field response-filter"
            role="group"
            aria-labelledby="response-filter-label"
          >
            <span id="response-filter-label" class="filter-label"
              >記入完了</span
            >
            <mat-checkbox
              [(ngModel)]="notAnsweredOnly"
              (ngModelChange)="onFiltersChanged()"
              >未完了のみ</mat-checkbox
            >
          </div>

          <fieldset
            class="metric-filter-group"
            [style.--metric-filter-count]="dashboard.items.length"
          >
            <legend>目標達成状況</legend>
            <div class="metric-filters">
              @for (item of dashboard.items; track item.id) {
                <label class="filter-field">
                  <span class="filter-label">{{ item.kpiName }}</span>
                  <select
                    [ngModel]="metricStatuses[item.id]"
                    (ngModelChange)="onMetricStatusChanged(item.id, $event)"
                  >
                    <option value="all">すべて</option>
                    <option value="achieved">達成</option>
                    <option value="unachieved">未達</option>
                  </select>
                </label>
              }
              <div class="filter-field logic-filter">
                <span id="metric-logic-label" class="filter-label">条件</span>
                <div
                  class="segment-control"
                  role="group"
                  aria-labelledby="metric-logic-label"
                >
                  <button
                    class="segment-button"
                    type="button"
                    [class.is-active]="metricLogic === 'and'"
                    [attr.aria-pressed]="metricLogic === 'and'"
                    (click)="selectMetricLogic('and')"
                  >
                    AND
                  </button>
                  <button
                    class="segment-button"
                    type="button"
                    [class.is-active]="metricLogic === 'or'"
                    [attr.aria-pressed]="metricLogic === 'or'"
                    (click)="selectMetricLogic('or')"
                  >
                    OR
                  </button>
                </div>
              </div>
            </div>
          </fieldset>
        </div>
      </div>
    </section>

    <div class="filter-result" aria-live="polite">
      {{ displayedRows.length }}名を表示（全{{ totalMemberCount }}名）
    </div>

    <div class="card table-wrap" [attr.aria-busy]="isLoading">
      @if (isLoading) {
        <div class="table-loading-overlay" aria-live="polite">
          <mat-spinner diameter="24" aria-hidden="true"></mat-spinner>
          集計を更新中です。
        </div>
      }
      <table class="operator-table" aria-label="オペレーター実績一覧">
        <thead>
          <tr>
            <th rowspan="2">氏名</th>
            <th rowspan="2">役職</th>
            @for (item of dashboard.items; track item.id) {
              <th class="group-head" colspan="2">
                {{ item.kpiName }}
              </th>
            }
            <th rowspan="2" [attr.aria-sort]="ariaSort('score', null)">
              <button
                class="sort-button"
                type="button"
                (click)="onSort('score', null)"
              >
                直近のアセスメントスコア
                <span class="sort-indicator" aria-hidden="true">{{
                  sortIndicator('score', null)
                }}</span>
              </button>
            </th>
            <th rowspan="2">記入完了</th>
            <th rowspan="2">育成設定</th>
          </tr>
          <tr>
            @for (item of dashboard.items; track item.id) {
              <th [attr.aria-sort]="ariaSort('actualRate', item.id)">
                <button
                  class="sort-button"
                  type="button"
                  (click)="onSort('actualRate', item.id)"
                >
                  実績
                  <span class="sort-indicator" aria-hidden="true">{{
                    sortIndicator('actualRate', item.id)
                  }}</span>
                </button>
              </th>
              <th [attr.aria-sort]="ariaSort('targetRate', item.id)">
                <button
                  class="sort-button"
                  type="button"
                  (click)="onSort('targetRate', item.id)"
                >
                  目標
                  <span class="sort-indicator" aria-hidden="true">{{
                    sortIndicator('targetRate', item.id)
                  }}</span>
                </button>
              </th>
            }
          </tr>
        </thead>
        <tbody>
          @if (summary; as currentSummary) {
            <tr class="operator-all-row">
              <td class="all-label" colspan="2">
                {{ dashboard.name }} ALL
                <small>
                  {{
                    filtersApplied
                      ? '現在表示中のオペレーターから再集計・目標は組織目標'
                      : '期間内の全入稿レコード集計・目標は組織目標'
                  }}
                </small>
                @if (currentSummary.orgTargetNote; as orgTargetNote) {
                  <small>{{ orgTargetNote }}</small>
                }
              </td>
              @for (cell of currentSummary.metricCells; track cell.itemId) {
                <td class="metric-cell">
                  @if (cell.hasActual) {
                    <div class="actual-result">
                      <span class="metric-count">{{ cell.countText }}</span>
                      <strong class="metric-value">
                        {{ cell.actualRateText }}
                      </strong>
                      <span class="delta" [class]="cell.gapClass">
                        {{ cell.gapText }}
                      </span>
                      @if (cell.achievement != null) {
                        <span
                          class="achievement-badge"
                          [class.achieved]="cell.achievement === true"
                          [class.unachieved]="cell.achievement === false"
                        >
                          {{ cell.achievementText }}
                        </span>
                      }
                    </div>
                  } @else {
                    <span class="empty">—</span>
                  }
                </td>
                <td class="target-cell">
                  {{ cell.targetRateText }}
                  <small>個人平均 {{ cell.individualTargetRateText }}</small>
                </td>
              }
              <td class="summary-score">
                <strong>{{ currentSummary.averageScoreText }}</strong>
                <small>メンバー横断・直近{{ appliedScoreCount }}回平均</small>
              </td>
              <td class="response-status-cell"><span class="empty">—</span></td>
              <td class="exclusion-cell"><span class="empty">—</span></td>
            </tr>
          }

          @for (row of displayedRows; track row.member.userId) {
            <tr
              class="operator-row"
              [class.is-excluded]="row.member.excludedFromCoaching"
              tabindex="0"
              role="link"
              [attr.aria-label]="row.member.userName + 'の個人支援ボードを開く'"
              (click)="openOperator(row.member.userId)"
              (keydown)="onRowKeydown($event, row.member.userId)"
            >
              <td class="operator-cell">
                <div class="operator-name">{{ row.member.userName }}</div>
                @if (row.member.excludedFromCoaching) {
                  <span class="operator-badge">育成対象外</span>
                }
              </td>
              <td class="role-cell">{{ row.roleLabel }}</td>
              @for (cell of row.metricCells; track cell.itemId) {
                <td class="metric-cell">
                  @if (cell.hasActual) {
                    <div class="actual-result">
                      <span class="metric-count">{{ cell.countText }}</span>
                      <strong class="metric-value">
                        {{ cell.actualRateText }}
                      </strong>
                      <span class="delta" [class]="cell.gapClass">
                        {{ cell.gapText }}
                      </span>
                      @if (cell.achievement != null) {
                        <span
                          class="achievement-badge"
                          [class.achieved]="cell.achievement === true"
                          [class.unachieved]="cell.achievement === false"
                        >
                          {{ cell.achievementText }}
                        </span>
                      }
                    </div>
                  } @else {
                    <span class="empty">—</span>
                  }
                </td>
                <td class="target-cell">
                  {{ cell.targetRateText }}
                </td>
              }
              <td class="sparkline-cell">
                @if (row.sparklineOptions; as sparklineOptions) {
                  <div class="sparkline">
                    <highcharts-chart
                      class="sparkline-chart"
                      [Highcharts]="Highcharts"
                      [options]="sparklineOptions"
                    ></highcharts-chart>
                    <div>
                      <div class="spark-number">
                        {{ row.latestScoreText }}
                      </div>
                      <div class="spark-caption">
                        最新<br />全{{ row.member.totalAssessmentCount }}回中
                        直近{{ row.member.scores.length }}回
                      </div>
                    </div>
                    <mat-icon class="row-arrow" aria-hidden="true">
                      chevron_right
                    </mat-icon>
                  </div>
                } @else {
                  <span class="empty">—</span>
                }
              </td>
              <td class="response-status-cell">
                <span [class]="row.responseStatusClass">{{
                  row.responseStatusLabel
                }}</span>
              </td>
              <td
                class="exclusion-cell"
                (click)="stopRowClick($event)"
                (keydown)="stopRowClick($event)"
              >
                <mat-checkbox
                  [checked]="row.member.excludedFromCoaching"
                  [disabled]="row.exclusionPending"
                  [attr.aria-label]="
                    row.member.userName +
                    (row.member.excludedFromCoaching
                      ? 'を育成対象に戻す'
                      : 'を育成対象外にする')
                  "
                  (change)="onExclusionChanged(row.member, $event)"
                >
                  対象外
                </mat-checkbox>
              </td>
            </tr>
          } @empty {
            <tr class="operator-empty-row">
              <td [attr.colspan]="columnCount">
                該当するオペレーターがいません。
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>

    <p class="field-help">
      ※
      実績未入稿のオペレーターと目標未設定の項目は「—」で表示します。スパークラインは指定した直近回数を左から古い順に表示します。
    </p>
  }
</main>
